import { useEffect, useState, useCallback } from "react";
import { getContent, postLead, goUrl } from "../lib/api";
import { FeatureIcon } from "../components/Icons";
import { BrandLogo, Logo } from "../components/Logo";
import Background from "../components/Background";
import { applyTheme, DEFAULT_THEME } from "../lib/theme";
import Quiz from "../components/Quiz";
import AgeGate from "../components/AgeGate";
import CookieConsent from "../components/CookieConsent";
import { LegalModal, RedirectModal } from "../components/Modals";
import { useReveal } from "../hooks/useReveal";

const Section = ({ children, id, className = "" }) => {
  const [ref, shown] = useReveal();
  return (
    <section id={id} ref={ref} className={`reveal ${shown ? "in" : ""} ${className}`}>
      {children}
    </section>
  );
};

export default function Landing() {
  const [content, setContent] = useState(null);
  const [legal, setLegal] = useState(null);
  const [redirectOpen, setRedirectOpen] = useState(false);

  useEffect(() => {
    getContent()
      .then((data) => {
        // Preview mode: apply unsaved theme from admin if present
        const params = new URLSearchParams(window.location.search);
        let theme = data.theme || DEFAULT_THEME;
        if (params.get("preview") === "1") {
          try {
            const pv = JSON.parse(localStorage.getItem("cosmic_preview_theme"));
            if (pv) theme = pv;
          } catch (e) {}
        }
        applyTheme(theme);
        setContent({ ...data, theme });
      })
      .catch(() => {});
  }, []);

  const scrollTo = useCallback((sel) => {
    const el = document.querySelector(sel);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, []);

  const onDownload = (lead) => {
    postLead(lead);
    setRedirectOpen(true);
  };

  const confirmRedirect = () => {
    try {
      if (window.ym) { window.ym(110962489, "reachGoal", "download_click"); }
    } catch (e) {}
    window.location.href = goUrl;
  };

  if (!content) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="grad-text font-head text-2xl">Загрузка…</div>
      </div>
    );
  }

  const { brand, header, hero, stats, features, audience, quiz, final, modal, legal: legalData, footer, theme } = content;
  const headerBg = "color-mix(in srgb, var(--bg) 78%, transparent)";

  return (
    <div className="App relative">
      <Background style={theme?.background_style} />
      <AgeGate content={content} />

      {/* Header */}
      <header
        className="sticky top-0 z-50"
        style={{ background: headerBg, backdropFilter: "blur(14px)", borderBottom: "1px solid var(--glass-border)" }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2" data-testid="logo">
            <BrandLogo theme={theme} size={26} nameSize="1.25rem" />
          </a>
          <nav className="flex items-center gap-1 sm:gap-4">
            {(header?.nav || []).map((n) => (
              <button
                key={n.anchor}
                onClick={() => scrollTo(n.anchor)}
                className="hidden sm:block text-sm px-2 py-1"
                style={{ color: "var(--text-sec)" }}
                data-testid={`nav-${n.anchor}`}
              >
                {n.label}
              </button>
            ))}
            <button className="btn-grad px-4 sm:px-5 py-2 text-sm" onClick={() => scrollTo("#quiz")} data-testid="header-cta">
              {header?.cta_text}
            </button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <div id="top" className="hero-glow relative overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-20 sm:pt-24 sm:pb-28 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {hero?.eyebrow && (
                <div className="inline-block mb-5 px-4 py-1.5 glass text-xs tracking-[0.2em] uppercase font-bold" style={{ color: "var(--text-sec)" }} data-testid="hero-eyebrow">
                  {hero.eyebrow}
                </div>
              )}
              <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6" data-testid="hero-title">
                {hero?.title}
              </h1>
              <p className="text-base sm:text-lg mb-9 max-w-xl" style={{ color: "var(--text-sec)" }} data-testid="hero-subtitle">
                {hero?.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="btn-grad cta-pulse px-8 py-4 text-base" onClick={() => scrollTo("#quiz")} data-testid="hero-cta-primary">
                  {hero?.cta_primary}
                </button>
                <button className="btn-ghost px-8 py-4 text-base" onClick={() => scrollTo("#about")} data-testid="hero-cta-secondary">
                  {hero?.cta_secondary}
                </button>
              </div>
            </div>

            {/* Match card */}
            <div className="flex justify-center lg:justify-end">
              <div className="glass p-5 w-full max-w-sm" data-testid="hero-match-card">
                <div className="relative rounded-2xl overflow-hidden mb-4" style={{ aspectRatio: "4/5" }}>
                  <img
                    src="/hero.webp"
                    alt="match"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3 px-3 py-1.5 rounded-full text-sm font-bold" style={{ background: "var(--grad)", boxShadow: "0 0 18px rgba(236,72,153,0.5)" }}>
                    {hero?.match_badge}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-head font-bold text-lg">{hero?.match_name}</div>
                    <div className="text-sm" style={{ color: "var(--text-sec)" }}>{hero?.match_caption}</div>
                  </div>
                  <Logo theme={theme} size={30} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <Section className="max-w-6xl mx-auto px-4 sm:px-6 -mt-6 mb-4">
        <div className="glass grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-4 py-8 px-6" data-testid="stats-bar">
          {(stats || []).map((s, i) => (
            <div key={i} className="text-center">
              <div className="font-head text-4xl sm:text-5xl font-bold" style={{ color: "var(--gold)" }} data-testid={`stat-value-${i}`}>
                {s.value}
              </div>
              <div className="text-sm mt-1" style={{ color: "var(--text-sec)" }}>{s.label}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Features */}
      <Section id="about" className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
        <div className="max-w-2xl mb-12">
          <h2 className="font-head text-3xl sm:text-4xl font-bold mb-3" data-testid="features-title">{content.features_title}</h2>
          <p style={{ color: "var(--text-sec)" }}>{content.features_subtitle}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {(features || []).map((f, i) => (
            <div key={i} className="glass p-6 h-full transition-transform hover:-translate-y-1" style={{ transition: "transform .25s ease" }} data-testid={`feature-card-${i}`}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(244,114,182,0.1)", border: "1px solid var(--glass-border)" }}>
                <FeatureIcon name={f.icon} />
              </div>
              <h3 className="font-head text-lg font-bold mb-2">{f.title}</h3>
              <p className="text-sm" style={{ color: "var(--text-sec)" }}>{f.text}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Audience */}
      <Section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20 sm:pb-28">
        <div className="max-w-2xl mb-12">
          <h2 className="font-head text-3xl sm:text-4xl font-bold mb-3" data-testid="audience-title">{content.audience_title}</h2>
          <p style={{ color: "var(--text-sec)" }}>{content.audience_subtitle}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {(audience || []).map((a, i) => (
            <div key={i} className="glass p-6" data-testid={`audience-card-${i}`}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center mb-3 font-head font-bold text-sm" style={{ background: "var(--grad)" }}>
                {i + 1}
              </div>
              <h3 className="font-head text-lg font-bold mb-2">{a.title}</h3>
              <p className="text-sm" style={{ color: "var(--text-sec)" }}>{a.text}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Quiz */}
      <Section id="quiz" className="max-w-2xl mx-auto px-4 sm:px-6 pb-24 scroll-mt-20">
        <div className="text-center mb-10">
          <h2 className="font-head text-3xl sm:text-4xl font-bold mb-3">{quiz?.title}</h2>
          <p style={{ color: "var(--text-sec)" }}>{quiz?.subtitle}</p>
        </div>
        <Quiz quiz={quiz} final={final} onDownload={onDownload} />
      </Section>

      {/* Footer */}
      <footer className="border-t" style={{ borderColor: "var(--glass-border)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-center sm:text-left">
            <div className="flex items-center gap-2 justify-center sm:justify-start mb-2">
              <BrandLogo theme={theme} size={22} nameSize="1.125rem" />
            </div>
            <p className="text-sm" style={{ color: "var(--text-sec)" }}>{footer?.tagline}</p>
          </div>
          <div className="flex gap-5 text-sm">
            <button onClick={() => setLegal(legalData?.privacy)} style={{ color: "var(--text-sec)" }} data-testid="footer-privacy">Конфиденциальность</button>
            <button onClick={() => setLegal(legalData?.terms)} style={{ color: "var(--text-sec)" }} data-testid="footer-terms">Условия</button>
            <button onClick={() => setLegal(legalData?.support)} style={{ color: "var(--text-sec)" }} data-testid="footer-support">Поддержка</button>
          </div>
        </div>
        <div className="text-center pb-8 text-xs" style={{ color: "var(--text-sec)" }} data-testid="footer-copyright">
          {footer?.copyright}
        </div>
      </footer>

      <LegalModal open={!!legal} data={legal} onClose={() => setLegal(null)} />
      <RedirectModal open={redirectOpen} modal={modal} onClose={() => setRedirectOpen(false)} onConfirm={confirmRedirect} />
      <CookieConsent content={content} onOpenPrivacy={() => setLegal(legalData?.privacy)} />
    </div>
  );
}
